import type { JobHeader, JobState } from '../schemas/bulk-migration';

/**
 * A job's progress and the polling around it, as plain data.
 *
 * The runner processes one queue message per tenant, so a job is never
 * "finished in the request". The screen polls the header while it is active and
 * stops when it is not — the release operations' cadence (`operation-status.ts`
 * in the catalog), for the same reasons.
 */

/** While a job is queued, planning or running. */
export const POLL_ACTIVE_MS = 3000;
/** After a failed read, so a blip recovers without a reload. */
export const POLL_AFTER_ERROR_MS = 30_000;
/**
 * No progress for this long and the screen says so. A tenant message that died
 * twice sits in the runner's dead-letter queue and never moves `done`; without
 * this the bar would just stop, looking like work in progress.
 */
export const UNRESPONSIVE_AFTER_MS = 10 * 60_000;
/** How often a launch re-reads its runs while any of them is still going. */
export const AUTO_REFRESH_MS = 30_000;

export function isActive(state: JobState): boolean {
    return state === 'queued' || state === 'planning' || state === 'running';
}

/** `refetchInterval` for a job's header. A 404 (`missing`) ends it. */
export function jobPollInterval(
    job: Pick<JobHeader, 'state'> | undefined,
    failure: 'none' | 'missing' | 'error' = 'none',
): number | false {
    if (failure === 'missing') return false;
    if (job === undefined) return failure === 'error' ? POLL_AFTER_ERROR_MS : POLL_ACTIVE_MS;
    return isActive(job.state) ? POLL_ACTIVE_MS : false;
}

export interface JobProgressView {
    /** "12 of 340 clients", or a sentence while the total is not known. */
    readonly label: string;
    /** 0–100, or `null` while there is no total to measure against. */
    readonly percent: number | null;
}

const STATE_SENTENCE: Readonly<Record<JobState, string>> = {
    queued: 'Queued — waiting for the runner to pick it up.',
    planning: 'Listing clients…',
    running: 'Working…',
    done: 'Finished.',
    failed: 'Failed.',
};

export function jobProgress(job: Pick<JobHeader, 'state' | 'done' | 'total'>): JobProgressView {
    if (job.total === null) return { label: STATE_SENTENCE[job.state], percent: null };
    const percent = job.total === 0 ? 100 : Math.min(100, (job.done / job.total) * 100);
    return {
        label: `${String(job.done)} of ${String(job.total)} ${job.total === 1 ? 'client' : 'clients'}`,
        percent,
    };
}

/**
 * Whether an active job has stopped moving. Measured from the last progress, or
 * — before any — from when it started or was created.
 */
export function isUnresponsive(
    job: Pick<JobHeader, 'state' | 'lastProgressAt' | 'startedAt' | 'createdAt'>,
    now: number,
): boolean {
    if (!isActive(job.state)) return false;
    const since = Date.parse(job.lastProgressAt ?? job.startedAt ?? job.createdAt);
    return Number.isFinite(since) && now - since > UNRESPONSIVE_AFTER_MS;
}

/** Runs a launch started that may still be moving: `running`, or `launched` and never re-read. */
export function liveRunCount(job: Pick<JobHeader, 'mode' | 'counts'>): number {
    if (job.mode === 'report') return job.counts.running ?? 0;
    if (job.mode === 'launch') return job.counts.launched ?? 0;
    return 0;
}

/**
 * Whether the launch view should ask the runner for a fresh report now.
 *
 * Only for someone who may (`canRefresh`), only once the launch itself is
 * done, never while a refresh is still being prepared, and then every
 * {@link AUTO_REFRESH_MS} while any run is live. The first refresh after a
 * launch is due at once: until then nothing has read the runs at all.
 */
export function autoRefreshDue(input: {
    readonly canRefresh: boolean;
    readonly launch: Pick<JobHeader, 'mode' | 'state' | 'counts'>;
    readonly latestReport: Pick<JobHeader, 'mode' | 'state' | 'counts' | 'finishedAt'> | undefined;
    readonly refreshing: boolean;
    readonly now: number;
}): boolean {
    const { canRefresh, launch, latestReport, refreshing, now } = input;
    if (!canRefresh || refreshing || launch.state !== 'done') return false;
    if (latestReport === undefined) return liveRunCount(launch) > 0;
    if (latestReport.state !== 'done') return false;
    if (liveRunCount(latestReport) === 0) return false;

    const finished = latestReport.finishedAt === null ? NaN : Date.parse(latestReport.finishedAt);
    return !Number.isFinite(finished) || now - finished >= AUTO_REFRESH_MS;
}
